import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, Subject, BehaviorSubject, of } from 'rxjs';
import { RefValue } from 'base-shared';
import { isArray } from 'lodash';

export interface ParentRefvalue {
  id: number;
  attributesSchema: string[];
  description: string;
  label: string;
  parentRefType: string;
  refValue: [RefValue];
}

@Injectable({
  providedIn: "root",
})
export class ProfileManagementService {

  public selectedIndex = 0;
  // public selectedIndex$ = new BehaviorSubject<number>(0);
  public selectedIndexView = 0;
  public disableSchedule = true;
  public headerStatus = "processing";
  public detailsStatus = "processing";
  public treatmentStatus = "processing";
  public aeoStatus = "processing";
  public historyStatus = "processing";
  public formStatus: any;
  public sub = new BehaviorSubject<any>(null);
  currentAction: string;

  private isTreatmentScoreMandatory = false;
  private rStatus = new Subject<boolean>();
  private copiedProfileData = new BehaviorSubject<any>(null);
  private amendRiskData = new BehaviorSubject<any>(null);

  constructor(private http: HttpClient) { }

  // Base URL builders
  public get intelligenceBaseUrl(): string {
    return `${environment.baseUrl}${environment.wso2amEndPoint}/intelligenceman${environment.wso2amApiVersion}/api/v1`;
  }

  private get aeoBaseUrl(): string {
    return `${environment.baseUrl}${environment.wso2amEndPoint}/aeoman${environment.wso2amApiVersion}/api/v1`;
  }

  private get rdmBaseUrl(): string {
    return `${environment.baseUrl}${environment.wso2amEndPoint}/rdm${environment.wso2amApiVersion}/api/v1`;
  }

  private get clmBaseUrl(): string {
    return `${environment.baseUrl}${environment.wso2amEndPoint}/clm${environment.wso2amApiVersion}/api/v1`;
  }

  private get bpsBaseUrl(): string {
    return `${environment.baseUrl}${environment.wso2amEndPoint}/bps${environment.wso2amApiVersion}`;
  }

  private get pcaBaseUrl(): string {
    return `${environment.baseUrl}${environment.wso2amEndPoint}/postclearanceaudit${environment.wso2amApiVersion}/api/v1`;
  }

  // Utility methods
  private buildHttpParams(params: any): HttpParams {
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        httpParams = httpParams.set(key, params[key].toString());
      }
    });
    return httpParams;
  }

  // Form status management
  setformStatus(ob: any): void {
    this.formStatus = ob;
    this.sub.next(ob);
  }

  setTabindex(num: number): void {
    this.selectedIndex = num;
  }

  getResetForm(): Observable<boolean> {
    return this.rStatus.asObservable();
  }

  resetForm(message: boolean): void {
    this.rStatus.next(message);
  }

  // Copy profile data methods
  setCopiedProfileData(data: any): void {
    this.copiedProfileData.next(data);
  }

  getCopiedProfileData(): Observable<any> {
    return this.copiedProfileData.asObservable();
  }

  clearCopiedProfileData(): void {
    this.copiedProfileData.next(null);
  }

  setAmendRiskData(data: any): void {
    this.amendRiskData.next(data);
  }

  getAmendRiskData(): Observable<any> {
    return this.amendRiskData.asObservable();
  }

  clearAmendRiskData(): void {
    this.amendRiskData.next(null);
  }

  // Treatment score getter/setter
  get treatmentScore(): boolean {
    return this.isTreatmentScoreMandatory;
  }

  set treatmentScore(isMandatory: boolean) {
    this.isTreatmentScoreMandatory = isMandatory;
  }

  // Profile Management APIs
  profileSearch(
    profileCode?: string,
    profileName?: string,
    profileType?: string,
    profileCategory?: string,
    riskInformationReqNumber?: string,
    profileRiskArea?: string,
    profileOwner?: string,
    profileStatus?: string,
    validFrom?: string,
    validTo?: string,
    page?: number,
    size?: number,
    sort?: string
  ): Observable<any> {
    const params = this.buildHttpParams({
      profileCode,
      profileName,
      profileType,
      profileCategory,
      riskInformationReqNumber,
      profileRiskArea,
      profileOwner,
      profileStatus,
      validFrom,
      validTo,
      page,
      size,
      sort,
    });

    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/search`, { params });
  }


  getProfileCode(): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/profilecode`);
  }

  getProfileName(profileName: string): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/getProfileName/${profileName}`);
  }

  postRiskProfileHeader(payload: any, user: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile?submittedBy=${user}`, payload);
  }

  updateRiskProfileHeader(payload: any, profileId: any, operationType: any, user: any): Observable<any> {
    return this.http.put(`${this.intelligenceBaseUrl}/riskprofile/ProfileHeader/${profileId}?action=${operationType}&submittedBy=${user}`, payload);
  }

  postRiskProfileDetails(payload: any, profileId: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile/profileDetails/${profileId}`, payload);
  }

  updateRiskProfileDetails(payload: any, profileId: any, operationType: any): Observable<any> {
    return this.http.put(`${this.intelligenceBaseUrl}/riskprofile/updateProfileDetails/${profileId}`, payload);
  }

  postRiskProfileTreatment(payload: any, profileId: any, user: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile/profileTreatment/${profileId}?submittedBy=${user}`, payload);
  }

  updateRiskProfileTreatment(payload: any, profileId: any, operationType: any): Observable<any> {
    return this.http.put(`${this.intelligenceBaseUrl}/riskprofile/updateProfileTreatment/${profileId}`, payload);
  }

  postRiskProfileSchedule(payload: any, profileId: any, user: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile/profileSchedulerDetails/${profileId}?submittedBy=${user}`, payload);
  }

  postRiskProfileAEO(payload: any, profileId: any, user: any, save: boolean): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile/profileAEO/${profileId}?submittedBy=${user}&save=${save}`, payload);
  }

  updateRiskProfileAEO(payload: any, profileId: any, user: any, operationType: any, save: boolean): Observable<any> {
    return this.http.put(`${this.intelligenceBaseUrl}/riskprofile/updateProfileAEO/${profileId}?submittedBy=${user}&save=${save}&operationType=${operationType}`, payload);
  }

  updateProfileTreatment(payload: any, profileId: any, user: any, save: boolean): Observable<any> {
    return this.http.put(`${this.intelligenceBaseUrl}/riskprofile/submitProfile/${profileId}?submittedBy=${user}&save=${save}`, payload);
  }

  updateProfileStatus(payload: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile/updateStatus`, payload);
  }

  uploadCsvFile(formData:any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/dmn/upload/indicators-values?type=simple`, formData);
  }

  deleteProfileID(profileId: string): Observable<any> {
    return this.http.delete(`${this.intelligenceBaseUrl}/riskprofile/${profileId}`);
  }

  deleteProfilesById(ids: any): Observable<any> {
    return this.http.request('delete', `${this.intelligenceBaseUrl}/riskprofile/profile/id`, { body: ids });
  }

  amendData(profileId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/amend/${profileId}`);
  }

  // Profile Data Retrieval
  headerData(profileId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/getProfileHeader/${profileId}`);
  }

  detailsData(profileId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/getProfileDetails/${profileId}`);
  }

  treatmentData(profileId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/getProfileTreatment/${profileId}`);
  }

  aeoData(profileId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/getProfileAEO/${profileId}`);
  }

  updateAeoById(id: number, payload: any): Observable<any> {
    return this.http.put(`${this.intelligenceBaseUrl}/aeointelligen/update/${id}`, payload);
  }

  downloadAeoTemplate(): Observable<Blob> {
    return this.http.get(`${this.intelligenceBaseUrl}/aeointelligen/download-template`, { responseType: 'blob' });
  }

  uploadExclFile(formData:any, user: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/aeointelligen/upload-xls?submittedBy=${user}`, formData);
  }

  downloadErrorReport(fileName: string, user: string): Observable<Blob> {
    return this.http.get(`${this.intelligenceBaseUrl}/aeointelligen/download-error-report/${fileName}?submittedBy=${user}`, { responseType: 'blob' });
  }

  getProfileHistory(profileCode: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/getProfileHistory/${profileCode}`);
  }
  checkAmend(profileId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/${profileId}/canAmend`);
  }

  getSandboxProfile(profileId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/profilesandbox/${profileId}`);
  }

  getSandboxResults(profileId: number, sandboxId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/profilesandbox/results/${profileId}?sandboxId=${sandboxId}`);
  }

  // Risk Management APIs
  riskSearch(
    riskCode?: string,
    infoSource?: string,
    status?: string,
    riskArea?: string,
    validFrom?: string,
    validTo?: string,
    countryOfOrigin?: string,
    source?: string,
    page?: number,
    size?: number,
    sort?: string,
    name?: string
  ): Observable<any> {
    const params = this.buildHttpParams({
      riskCode,
      infoSource,
      status,
      riskArea,
      validFrom,
      validTo,
      countryOfOrigin,
      source,
      page,
      size,
      sort,
      name,
    });

    return this.http.get(`${this.intelligenceBaseUrl}/risk/information/search`, { params });
  }

  postCreateRiskHeader(payload: any, operation: any, user: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/risk/information?operation=${operation}&role=${user}`, payload);
  }

  updateRiskinfoHeader(payload: any, riskId: any, user: any): Observable<any> {
   return this.http.put(`${this.intelligenceBaseUrl}/risk/information/request/review/${riskId}?role=${user}`, payload);
  }

  checkAmendRiskInfo(riskId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/risk/information/${riskId}/canAmend`);
  }

  getRiskDetail(riskId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/risk/information/view/${riskId}`);
  }

  getRiskHistory(riskId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/risk/information/history/${riskId}`);
  }

  recomendedOutcome(riskId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/risk/information/outcomes/${riskId}`);
  }

  // deleteRiskInfo(riskId: string, status:any): Observable<any> {
  //   const params = this.buildHttpParams({ status: status, requestInformationId: riskId });
  //   return this.http.put(`${this.intelligenceBaseUrl}/risk/information/status`, {}, { params });
  // }

  hardDeleteRiskInfo(requestInformationId: string): Observable<any> {
    const params = this.buildHttpParams({ requestInformationId });
    return this.http.delete(`${this.intelligenceBaseUrl}/risk/information/id`, { params });
  }

  riskFactor(payload: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/risk/riskFactor`, payload);
  }

  getriskFactor(): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/risk/riskFactor`);
  }

  getRiskCategories(): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/risk/categories`);
  }

  getRiskIndicators(type: string): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/risk/indicators/${type}`);
  }

  // External Source Risk Information
  processExternalSourceForRiskInfo(isPreview: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/risk/information/processExternalSourceForRiskInfo/${isPreview}`);
  }

  externalSourceRiskInfoSearchSearch(newsId?: string, page?: number, size?: number, sort?: string): Observable<any> {
    const params = this.buildHttpParams({ newsId, page, size, sort });
    return this.http.get(`${this.intelligenceBaseUrl}/risk/information/searchExternalSourceRiskInformations`, { params });
  }

  getExternalSourceRiskInfoByRiskInfoId(riskInfoId: any): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/risk/information/getExternalSourceRiskInformation/${riskInfoId}`);
  }

  searchExternalNews(source: string, page?: number, size?: number, sort?: string): Observable<any> {
    const params = this.buildHttpParams({ page, size, sort });
    return this.http.get(`${this.intelligenceBaseUrl}/risk/information/searchExternalNews/${source}`, { params });
  }

  // Trusted Traders Management
  getTrustedTraders(page?: number, size?: number, sortKey?: any): Observable<any> {
    const params = this.buildHttpParams({ page, size, sort: sortKey });
    return this.http.get(`${this.intelligenceBaseUrl}/trustedtraders`, { params });
  }

  getTrustedTrader(id?: number): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/aeointelligen/get-active/${id}`);
  }

  createTrustedTrader(payload: any, save: string): Observable<any> {
    const params = new HttpParams().set('save', save);
    return this.http.post(`${this.intelligenceBaseUrl}/trustedtraders`, payload, { params });
  }

  updateTrustedTraders(payload: any, id: any, user: any): Observable<any> {
    return this.http.put(`${this.intelligenceBaseUrl}/aeointelligen/update/${id}?submittedBy=${user}`, payload);
  }

  searchTraders(
    companyCode?: string,
    companyName?: string,
    economyCode?: string,
    trustedTraderCode?: string,
    licenceExpDate?: string,
    status?: string,
    createdDate?: string,
    page?: number,
    size?: number,
    sort?: string
  ): Observable<any> {
    const httpParams = this.buildHttpParams({
      companyCode,
      companyName,
      economyCode,
      trustedTraderCode,
      licenceExpDate,
      status,
      createdDate,
      page,
      size,
      sort,
    });

    return this.http.get(`${this.intelligenceBaseUrl}/trustedtraders/search`, { params: httpParams });
  }


  getTraderHistoryMap(id: any): Observable<any> {   
    return this.http.get(`${this.intelligenceBaseUrl}/aeointelligen/history/record/${id}`);
  }

  getTraderTrader(): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/trustedtraders/generateUniqueCode`, {});
  }

  // Express Courier Manifest

  getEcManifestBl(
    houseBLNo?: string,
    manifestNo?: string,
    transitType?: string,
    destinationPort?: string,
    inspectionAction?: string,
    page?: number,
    size?: number,
    sort?: string
  ): Observable<any> {
    const httpParams = this.buildHttpParams({
      houseBLNo,
      manifestNo,
      transitType,
      destinationPort,
      inspectionAction,
      page,
      size,
      sort,
    });

    return this.http.get(`${this.intelligenceBaseUrl}/riskAss/ec/manifest-bl/search`, { params: httpParams });
  }


  getECManifestByID(id: string): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/risk/ecmanifest/${id}`);
  }

  eCManifestBlSearch(ecManifestHouseBlNo: string, page?: number, size?: number, sort?: string): Observable<any> {
    const params = this.buildHttpParams({ ecManifestHouseBlNo, page, size, sort });
    return this.http.get(`${this.intelligenceBaseUrl}/riskAss/ec/manifest-bl/profile/search`, { params });
  }

  // Profile Execution and Reports
  searchProfileExecutionDetails(
    profileCode?: string,
    referenceCode?: number,
    fromDate?: string,
    toDate?: string,
    page?: number,
    size?: number,
    sort?: string
  ): Observable<any> {
    const httpParams = this.buildHttpParams({
      profileCode,
      referenceCode,
      fromDate,
      toDate,
      page,
      size,
      sort,
    });

    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/searchProfileExecutionDetail`, {
      params: httpParams,
    });
  }


  generateReport(profileCategory?: any, riskMitigation?: any, active?: any): Observable<Blob> {
    const params = this.buildHttpParams({ profileCategory, riskMitigation, active });
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/report`, { responseType: 'blob', params });
  }

  generateGreenReport(dateTo: any, hsCodes: any, dateFrom: any, profileCodes: any): Observable<Blob> {
    const params = this.buildHttpParams({ dateTo, hsCodes, dateFrom, profileCodes });
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/greencustomsreport`, { responseType: 'blob', params });
  }

  upload(payload: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile/riskProfileReport/upload`, payload);
  }

  downloadSample(fileName: string): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/download/${fileName}`, { responseType: 'blob' });
  }

  downloadActiveProfiles(fileName: string): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/riskProfileReport/download/${fileName}`, { responseType: 'blob' });
  }

  downloadProfilesById(id: number): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/riskprofile/riskProfileReport/download/${id}`, { responseType: 'blob' });
  }

  // Profile Categories and Indicators
  getProfileCategories(): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/dmn/getClassCategories`);
  }

  getProfileIndicators(category?: string): Observable<any> {
    const params = this.buildHttpParams({ category });
    return this.http.get(`${this.intelligenceBaseUrl}/dmn/profileIndicators`, { params });
  }

  // AEO Management
  getAeoDetail(page?: number, size?: number, sortKey?: any): Observable<any> {
    const params = this.buildHttpParams({ page, size, sort: sortKey });
    return this.http.get(`${this.aeoBaseUrl}/aeoman/search`, { params });
  }

  getAeoDetailNew(page?: number, size?: number, sortKey?: any, searchTerm?: string): Observable<any> {
    const params = this.buildHttpParams({ page, size, sort: sortKey, searchTerm });
    return this.http.get(`${environment.baseUrl}/${environment.wso2amEndPoint}intelligenceman/api/v1/aeoman/summary`, { params });
  }

  // Document Upload
  uploadRiskInfoDocuments(payload: any, id: any): Observable<any> {
    return this.http.post(`${this.aeoBaseUrl}/aeoman/upload/aeoFile/${id}`, payload);
  }

  uploadRiskDocuments(payload: any, id: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/risks/upload/file/${id}`, payload);
  }

  // RDM Services
  rdmByParentRefvalue(label: string, parent: string): Observable<ParentRefvalue> {
    return this.http.get<ParentRefvalue>(`${this.rdmBaseUrl}/types/parentRefvalue?label=${label}&parent=${parent}`);
  }

  getLabelfromDGRDM(payload: any, key: any): Observable<any> {
    return this.http.post(`${this.rdmBaseUrl}/labels?key=${key}`, payload);
  }

  // CLM Services
  getClmData(id: string): Observable<any> {
    return this.http.get(`${this.clmBaseUrl}/customers/customerlist/license/activitygroup/${id}`);
  }

  getTraderName(): Observable<any> {
    return this.http.get(`${this.clmBaseUrl}/customers/customerlist/license/activitygroup/Traders`);
  }

  // Task Management
  getTasks(firstResult: number, maxResults: number, payload: any): Observable<any> {
    const params = this.buildHttpParams({ firstResult, maxResults });
    return this.http.post(`${this.bpsBaseUrl}/rest/engine/default/task`, payload, { params });
  }

  // Post Clearance Audit
  PCADeskSearch(declarationNumber?: number, recommendedAuditType?: string): Observable<any> {
    const params = this.buildHttpParams({ declarationNumber, recommendedAuditType });
    return this.http.get(`${this.pcaBaseUrl}/pca/pcaSearch`, { params });
  }

  // Favourite List Management APIs
  favouriteListSearch(
    recordNo?: string,
    favouriteListName?: string,
    masterData?: string,
    status?: string,
    startDate?: string,
    endDate?: string,
    page?: number,
    size?: number,
    sort?: string,
  ): Observable<any> {
    const params = this.buildHttpParams({
      recordNo,
      favouriteListName,
      masterData,
      status,
      startDate,
      endDate,
      page,
      size,
      sort
    });
    return this.http.get(`${this.intelligenceBaseUrl}/favouriteList/search`, { params });
  }

  createFavouriteList(payload: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/favouriteList/create`, payload);
  }

  getFavouriteListById(recordNo: number): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/favouriteList/view/${recordNo}`);
  }

  getPendingProfiles(favouriteListId: number): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/favouriteList/pending-profiles/${favouriteListId}`);
  }

  hasAttachedProfiles(favouriteListId: number): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/favouriteList/has-attached-profiles/${favouriteListId}`);
  }

  updateFavouriteList(recordNo: number, payload: any): Observable<any> {
    return this.http.put(`${this.intelligenceBaseUrl}/favouriteList/update/${recordNo}`, payload);
  }

  uploadFavouriteListCodesFile(formData: any, masterData: string): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/favouriteList/validate-csv?refTypeLabel=${masterData}`, formData);
  }

  deleteFavouriteList(recordNo: number): Observable<any> {
    return this.http.delete(`${this.intelligenceBaseUrl}/favouriteList/${recordNo}`);
  }

  getGeneratedRecordNo(): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/favouriteList/generate-record-no`);
  }

  downloadFavouriteListTemplate(): Observable<any> {
    return this.http.get(`${this.intelligenceBaseUrl}/favouriteList/export-csv`, { responseType: 'blob' });
  }

  getRDMvalue(refTypeCode:any): Observable<any> {
     const url = `${environment.baseUrl}/rdm/api/v1/values/search/filter?refTypeLabel=${refTypeCode}`;
    return this.http.get(url);
  }

  // Utility method for consistent date formatting with HH:MM
  formatDateWithTime(dateValue: any): string {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  }

  validateBraces(payload: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile/validate-braces`, payload);
  }

  getCommodityHsCodesByElementName(hscode: string): Observable<any> {
    const hsCodeValue = Array.isArray(hscode) ? hscode[0]?.trim() : hscode?.split(',')[0]?.trim();
    return this.http.get(`${this.rdmBaseUrl}/hs-code-mapping/mapped-indicators/${hsCodeValue}`);
  }

  searchHsCodeMappingsByGroup(search: string, page: number, size: number, sort?: string): Observable<any> {
    const params = this.buildHttpParams({ search, page, size, sort: sort || 'indicatorGroupCode,asc' });
    return this.http.get(`${this.rdmBaseUrl}/hs-code-mapping/search-groups`, { params });
  }

  getHsCodesByMappingId(mappingId: string, page: number, size: number): Observable<any> {
    const params = this.buildHttpParams({ page, size, sort: 'hsCode,asc' });
    return this.http.get(`${this.rdmBaseUrl}/hs-code-mapping/${mappingId}/hs-codes`, { params });
  }

  checkDuplicateIndicators(profileCode: any, payload: any): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile/search-by-attributes?profileCode=${profileCode}`, payload);
  }

  getPerformanceReport(profileId: number, startDate: string, endDate: string): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile/performanceReport`, { profileId, dateRange: { startDate, endDate } });
  }

  getPerformanceReportEo(profileId: number, startDate: string, endDate: string): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile/performanceReport/eo`, { profileId, dateRange: { startDate, endDate } });
  }

  downloadChartPDF(formData: FormData): Observable<any> {
    return this.http.post(`${this.intelligenceBaseUrl}/riskprofile/performanceReport/download`,  formData );
  }
}